import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useTranslations } from 'use-intl';
import type { Locale } from '@/i18n';
import { localePath } from '@/app/locale';
import { api } from '@/lib/api';
import { AuthLayout } from './AuthLayout';
import { useAuthForm } from './useAuthForm';
import { Button } from '@/ui/Button';
import { Field, TextInput } from '@/ui/Field';
import { Notice } from '@/ui/Panel';

export function ResetPassword({ locale }: { locale: Locale }) {
  const t = useTranslations();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const { busy, formError, fieldError, submit } = useAuthForm();
  const [password, setPassword] = useState('');
  const [done, setDone] = useState(false);

  return (
    <AuthLayout
      locale={locale}
      title={t('auth.resetTitle')}
      lead={t('auth.resetLead')}
      footer={
        <Link to={localePath(locale, '/login')} className="text-ink-muted underline decoration-line-strong underline-offset-2 hover:text-ink">
          {t('auth.signIn')}
        </Link>
      }
    >
      {!token ? (
        <Notice tone="risk">{t('error.invalid_or_expired_token')}</Notice>
      ) : done ? (
        <Notice tone="ok">{t('auth.resetDone')}</Notice>
      ) : (
        <form
          noValidate
          onSubmit={async (e) => {
            e.preventDefault();
            const ok = await submit(() =>
              api.post('/auth/reset-password', { token, password }),
            );
            if (ok) setDone(true);
          }}
        >
          {formError ? (
            <div className="mb-4">
              <Notice tone="risk">{formError}</Notice>
            </div>
          ) : null}

          <Field
            label={t('auth.newPassword')}
            hint={t('auth.passwordHint')}
            error={fieldError('password')}
          >
            {(p) => (
              <TextInput
                {...p}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                minLength={10}
                required
                autoFocus
              />
            )}
          </Field>

          <Button type="submit" variant="primary" busy={busy} className="mt-2 w-full">
            {t('auth.setPassword')}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
